"use client"

import { LayoutDashboard, Package, ShoppingCart, Users, Settings, Menu, X, LogOut, Home } from "lucide-react"
import { useState, useEffect } from "react"
import { Link, Outlet, useLocation } from "react-router-dom"

const AdminLayout = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const location = useLocation()

  // Close menu when route changes on mobile
  useEffect(() => {
    setIsMenuOpen(false)
  }, [location])

  // Close menu when clicking outside on mobile
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const sidebar = document.getElementById('sidebar')
      const menuButton = document.getElementById('menu-button')
      if (isMenuOpen && sidebar && !sidebar.contains(event.target as Node) && 
          menuButton && !menuButton.contains(event.target as Node)) {
        setIsMenuOpen(false)
  }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isMenuOpen])

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Mobile Overlay */}
      {isMenuOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden" onClick={() => setIsMenuOpen(false)} />
      )}

      {/* Sidebar */}
      <div
        id="sidebar"
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-gray-800 text-white transform transition-transform duration-200 ease-in-out ${
          isMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <Link to="/admin" className="text-white flex items-center space-x-2">
          <Home className="h-6 w-6" />
          <span className="text-2xl font-extrabold">Dashboard</span>
        </Link>
          <button 
            id="menu-button"
            className="lg:hidden p-2 rounded-md hover:bg-gray-700 focus:outline-none"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <nav className="mt-6 px-4 space-y-2">
          <Link
            to="/admin"
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
              location.pathname === "/admin" ? "bg-gray-700" : "hover:bg-gray-700"
            }`}
          >
            <LayoutDashboard className="w-5 h-5" />
            <span>Dashboard</span>
          </Link>
          <Link
            to="/admin/products"
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
              location.pathname.startsWith("/admin/products") ? "bg-gray-700" : "hover:bg-gray-700"
            }`}
          >
            <Package className="w-5 h-5" />
            <span>Products</span>
          </Link>
          <Link
            to="/admin/orders"
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
              location.pathname.startsWith("/admin/orders") ? "bg-gray-700" : "hover:bg-gray-700"
            }`}
          >
            <ShoppingCart className="w-5 h-5" />
            <span>Orders</span>
          </Link>
          <Link
            to="/admin/users"
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
              location.pathname.startsWith("/admin/users") ? "bg-gray-700" : "hover:bg-gray-700"
            }`}
          >
            <Users className="w-5 h-5" />
            <span>User Management</span>
          </Link>
          <Link
            to="/admin/settings"
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
              location.pathname.startsWith("/admin/settings") ? "bg-gray-700" : "hover:bg-gray-700"
            }`}
          >
            <Settings className="w-5 h-5" />
            <span>Settings</span>
          </Link>
          <Link
            to="/admin/logout"
            className="flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors hover:bg-gray-700"
          >
            <LogOut className="w-5 h-5" />
            <span>Logout</span>
          </Link>
        </nav>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white shadow-sm">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
            <button 
              className="lg:hidden p-2 rounded-md hover:bg-gray-100 focus:outline-none"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              <Menu className="h-6 w-6 text-gray-600" />
            </button>
            <div className="flex items-center">
              {/* Profile dropdown or user info here */}
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-100">
          <div className="container mx-auto px-4 py-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  )
}

export default AdminLayout
